import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../api/axios';
import { REGISTRY_TARGET, runnerApi, errorText } from '../../api/migrationRunner';
import { CONSOLE_PREFILL_KEY } from './runner/nav';
import { Btn, Dialog } from './runner/ui';
import { useApprovalGate } from './runner/ApprovalGate';
import {
  CircleStackIcon,
  CommandLineIcon,
  MagnifyingGlassIcon,
  PlayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Company {
  id: string;
  company_name: string;
  database_name: string | null;
}

interface QueryTemplate {
  category: string;
  title: string;
  description: string;
  sql: string;
}

interface HistoryEntry {
  sql: string;
  companyId: string;
  companyName: string;
  rows: number;
  at: string; // ISO timestamp
}

// ---------------------------------------------------------------------------
// Query templates (unchanged from original)
// ---------------------------------------------------------------------------

const queryTemplates: QueryTemplate[] = [
  // 👥 USER & EMPLOYEE QUERIES
  {
    category: 'Users & Employees',
    title: 'Active Employees',
    description: 'All employees with portal access and their roles',
    sql: `SELECT
  employee_id,
  name,
  email,
  role,
  can_access_employee_portal,
  can_edit_own_shifts,
  max_shift_edit_days
FROM users
WHERE can_access_employee_portal = 1
ORDER BY name`
  },
  {
    category: 'Users & Employees',
    title: 'Locked Accounts',
    description: 'Employees with locked accounts and unlock times',
    sql: `SELECT
  employee_id,
  name,
  email,
  login_attempts,
  locked_until,
  TIMESTAMPDIFF(MINUTE, NOW(), CAST(locked_until AS DATETIME)) as minutes_until_unlock
FROM users
WHERE locked_until IS NOT NULL
  AND locked_until <> ''
  AND CAST(locked_until AS DATETIME) > NOW()
ORDER BY CAST(locked_until AS DATETIME) DESC`
  },
  {
    category: 'Users & Employees',
    title: 'Employee Summary',
    description: 'Total counts by role and portal access',
    sql: `SELECT
  role,
  COUNT(*) as total_employees,
  SUM(can_access_employee_portal) as portal_enabled,
  SUM(can_edit_own_shifts) as can_edit_shifts
FROM users
GROUP BY role
ORDER BY total_employees DESC`
  },

  // ⏰ SHIFT QUERIES
  {
    category: 'Shifts & Time',
    title: 'Recent Shifts (7 Days)',
    description: 'Latest shifts with hours worked and project details',
    sql: `SELECT
  DATE_FORMAT(s.clock_in, '%Y-%m-%d') as date,
  u.name as employee,
  u.role,
  p.name as project,
  DATE_FORMAT(s.clock_in, '%h:%i %p') as start_time,
  DATE_FORMAT(s.clock_out, '%h:%i %p') as end_time,
  CONCAT(
    FLOOR(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60), 'h ',
    MOD(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out), 60), 'm'
  ) as duration,
  ROUND(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60, 2) as total_hours,
  s.creation_source
FROM shifts s
LEFT JOIN users u ON s.employee_id = u.employee_id
LEFT JOIN projects p ON s.project_id = p.id
WHERE s.clock_in >= DATE_SUB(CURDATE(), INTERVAL 7 DAY)
  AND s.clock_out IS NOT NULL
ORDER BY s.clock_in DESC
LIMIT 100`
  },
  {
    category: 'Shifts & Time',
    title: 'Open Shifts',
    description: 'Shifts still clocked in without clock out',
    sql: `SELECT
  s.employee_id,
  u.name,
  DATE_FORMAT(s.clock_in, '%Y-%m-%d %h:%i %p') as clocked_in_at,
  CONCAT(
    FLOOR(TIMESTAMPDIFF(MINUTE, s.clock_in, NOW()) / 60), 'h ',
    MOD(TIMESTAMPDIFF(MINUTE, s.clock_in, NOW()), 60), 'm'
  ) as time_elapsed,
  ROUND(TIMESTAMPDIFF(MINUTE, s.clock_in, NOW()) / 60, 1) as hours_open,
  p.name as project,
  s.creation_source
FROM shifts s
LEFT JOIN users u ON s.employee_id = u.employee_id
LEFT JOIN projects p ON s.project_id = p.id
WHERE s.clock_out IS NULL
ORDER BY s.clock_in ASC`
  },
  {
    category: 'Shifts & Time',
    title: 'Hours by Employee (30 Days)',
    description: 'Total and average hours worked per employee',
    sql: `SELECT
  u.name,
  u.role,
  COUNT(s.id) as total_shifts,
  ROUND(SUM(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60), 1) as total_hours,
  ROUND(AVG(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60), 1) as avg_hours_per_shift,
  MAX(DATE(s.clock_in)) as last_shift
FROM shifts s
JOIN users u ON s.employee_id = u.employee_id
WHERE s.clock_out IS NOT NULL
  AND s.clock_in >= DATE_SUB(CURDATE(), INTERVAL 30 DAY)
GROUP BY u.employee_id, u.name, u.role
ORDER BY total_hours DESC`
  },
  {
    category: 'Shifts & Time',
    title: 'Overtime Analysis',
    description: 'Shifts over 8 hours in the last 30 days',
    sql: `SELECT
  DATE_FORMAT(s.clock_in, '%Y-%m-%d') as date,
  u.name,
  DATE_FORMAT(s.clock_in, '%h:%i %p') as start,
  DATE_FORMAT(s.clock_out, '%h:%i %p') as end,
  CONCAT(
    FLOOR(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60), 'h ',
    MOD(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out), 60), 'm'
  ) as duration,
  ROUND(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60, 2) as total_hours,
  ROUND((TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60) - 8, 2) as overtime_hours,
  p.name as project
FROM shifts s
JOIN users u ON s.employee_id = u.employee_id
LEFT JOIN projects p ON s.project_id = p.id
WHERE s.clock_out IS NOT NULL
  AND TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60 > 8
  AND s.clock_in >= DATE_SUB(CURDATE(), INTERVAL 30 DAY)
ORDER BY total_hours DESC
LIMIT 100`
  },
  {
    category: 'Shifts & Time',
    title: 'Manually Created Shifts',
    description: 'Shifts created by users or admins (not system clock-in)',
    sql: `SELECT
  DATE_FORMAT(s.clock_in, '%Y-%m-%d') as date,
  u.name as employee,
  DATE_FORMAT(s.clock_in, '%h:%i %p') as start_time,
  DATE_FORMAT(s.clock_out, '%h:%i %p') as end_time,
  CONCAT(
    FLOOR(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60), 'h ',
    MOD(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out), 60), 'm'
  ) as duration,
  ROUND(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60, 2) as total_hours,
  s.creation_source,
  s.created_by_employee_id,
  creator.name as created_by,
  DATE_FORMAT(s.creation_timestamp, '%Y-%m-%d %h:%i %p') as created_at
FROM shifts s
JOIN users u ON s.employee_id = u.employee_id
LEFT JOIN users creator ON s.created_by_employee_id = creator.employee_id
WHERE s.creation_source IN ('user_manual', 'admin_manual')
ORDER BY s.creation_timestamp DESC
LIMIT 100`
  },

  // 🚧 PROJECT QUERIES
  {
    category: 'Projects',
    title: 'Active Projects',
    description: 'All active and in-progress projects with timeline',
    sql: `SELECT
  name as project_name,
  job_number,
  status,
  start_date,
  end_date,
  DATEDIFF(end_date, CURDATE()) as days_remaining,
  DATEDIFF(CURDATE(), start_date) as days_elapsed,
  client_name
FROM projects
WHERE active = 1
  AND status IN ('in_progress', 'pending')
ORDER BY start_date DESC`
  },
  {
    category: 'Projects',
    title: 'Project Hours Summary',
    description: 'Total hours worked per project (last 30 days)',
    sql: `SELECT
  p.name as project_name,
  p.job_number,
  p.status,
  COUNT(DISTINCT s.employee_id) as unique_employees,
  COUNT(s.id) as total_shifts,
  ROUND(SUM(TIMESTAMPDIFF(MINUTE, s.clock_in, s.clock_out) / 60), 1) as total_hours,
  DATE_FORMAT(MAX(s.clock_in), '%Y-%m-%d') as last_activity,
  DATEDIFF(CURDATE(), MAX(s.clock_in)) as days_since_activity
FROM projects p
LEFT JOIN shifts s ON p.id = s.project_id
WHERE s.clock_out IS NOT NULL
  AND s.clock_in >= DATE_SUB(CURDATE(), INTERVAL 30 DAY)
GROUP BY p.id, p.name, p.job_number, p.status
ORDER BY total_hours DESC`
  },
  {
    category: 'Projects',
    title: 'Project Status Overview',
    description: 'Count of projects by status',
    sql: `SELECT
  status,
  COUNT(*) as project_count,
  SUM(active) as active_count,
  MIN(start_date) as earliest_start,
  MAX(end_date) as latest_end
FROM projects
GROUP BY status
ORDER BY
  CASE status
    WHEN 'in_progress' THEN 1
    WHEN 'pending' THEN 2
    WHEN 'completed' THEN 3
    ELSE 4
  END`
  },

  // 🚢 BARGE & DELIVERIES
  {
    category: 'Deliveries',
    title: 'Recent Customer Deliveries',
    description: 'Customer deliveries from the last 30 days',
    sql: `SELECT
  DATE_FORMAT(created_at, '%Y-%m-%d %h:%i %p') as delivery_time,
  material,
  CONCAT(quantity, ' ', unit) as amount,
  client_name,
  employee_id,
  DATEDIFF(CURDATE(), created_at) as days_ago,
  notes
FROM deliveries
WHERE created_at >= DATE_SUB(CURDATE(), INTERVAL 30 DAY)
ORDER BY created_at DESC
LIMIT 50`
  },
  {
    category: 'Deliveries',
    title: 'Customer Deliveries by Material',
    description: 'Customer deliveries grouped by material',
    sql: `SELECT
  material,
  COUNT(*) as delivery_count,
  SUM(quantity) as total_quantity,
  MIN(unit) as unit,
  DATE_FORMAT(MIN(created_at), '%Y-%m-%d') as first_delivery,
  DATE_FORMAT(MAX(created_at), '%Y-%m-%d') as last_delivery,
  DATEDIFF(MAX(created_at), MIN(created_at)) as days_span
FROM deliveries
GROUP BY material
ORDER BY total_quantity DESC`
  },
  {
    category: 'Deliveries',
    title: 'Deliveries by Client',
    description: 'Customer deliveries grouped by client',
    sql: `SELECT
  client_name,
  COUNT(*) as delivery_count,
  SUM(quantity) as total_delivered,
  DATE_FORMAT(MAX(created_at), '%Y-%m-%d %h:%i %p') as last_delivery,
  DATEDIFF(CURDATE(), MAX(created_at)) as days_since_last
FROM deliveries
WHERE client_name IS NOT NULL
GROUP BY client_name
ORDER BY last_delivery DESC`
  },
  {
    category: 'Deliveries',
    title: 'Shift Deliveries (Job)',
    description: 'Shift-based job deliveries with drop counts',
    sql: `SELECT
  sd.id,
  DATE_FORMAT(sd.created_at, '%Y-%m-%d %h:%i %p') as delivery_time,
  m.name as material,
  CONCAT(sd.quantity, ' ', sd.unit) as amount,
  sd.origin_type,
  sd.destination_type,
  COUNT(sdd.id) as drop_count,
  u.name as employee_name
FROM shift_deliveries sd
LEFT JOIN materials m ON sd.material_id = m.id
LEFT JOIN shift_delivery_drops sdd ON sd.id = sdd.shift_delivery_id
LEFT JOIN users u ON sd.employee_id = u.employee_id
GROUP BY sd.id, sd.created_at, m.name, sd.quantity, sd.unit, sd.origin_type, sd.destination_type, u.name
ORDER BY sd.created_at DESC
LIMIT 50`
  },

  // 🔧 EQUIPMENT
  {
    category: 'Equipment',
    title: 'Equipment Inventory',
    description: 'All equipment with service status and last service date',
    sql: `SELECT
  name as equipment_name,
  type as equipment_type,
  category,
  service_status,
  last_service_date,
  DATEDIFF(CURDATE(), DATE(last_service_date)) as days_since_service,
  next_service_due,
  current_meter_reading,
  meter_unit
FROM equipment
ORDER BY
  CASE service_status
    WHEN 'needs_repair' THEN 1
    WHEN 'overdue' THEN 2
    WHEN 'due_soon' THEN 3
    WHEN 'good' THEN 4
    ELSE 5
  END,
  days_since_service DESC`
  },
  {
    category: 'Equipment',
    title: 'Service Due / Overdue',
    description: 'Equipment with service_status of overdue or needs_repair',
    sql: `SELECT
  name as equipment_name,
  type as equipment_type,
  service_status,
  last_service_date,
  DATEDIFF(CURDATE(), DATE(last_service_date)) as days_since_service,
  next_service_due
FROM equipment
WHERE service_status IN ('overdue', 'needs_repair', 'due_soon')
ORDER BY
  CASE service_status
    WHEN 'needs_repair' THEN 1
    WHEN 'overdue' THEN 2
    WHEN 'due_soon' THEN 3
  END,
  days_since_service DESC`
  },
  {
    category: 'Equipment',
    title: 'Equipment by Service Status',
    description: 'Count of equipment grouped by service_status',
    sql: `SELECT
  service_status,
  COUNT(*) as equipment_count,
  COUNT(DISTINCT type) as unique_types
FROM equipment
GROUP BY service_status
ORDER BY equipment_count DESC`
  },
  {
    category: 'Equipment',
    title: 'Open Equipment Repairs',
    description: 'Pending or in-progress repair tickets, highest priority first',
    sql: `SELECT
  r.id,
  e.name as equipment,
  e.type as equipment_type,
  r.issue_type,
  r.priority,
  r.status,
  LEFT(r.description, 80) as description,
  reporter.name as reported_by,
  assignee.name as assigned_to,
  DATE_FORMAT(r.created_at, '%Y-%m-%d %h:%i %p') as opened_at,
  DATEDIFF(CURDATE(), DATE(r.created_at)) as days_open
FROM equipment_repairs r
LEFT JOIN equipment e ON r.equipment_id = e.id
LEFT JOIN users reporter ON r.employee_id = reporter.employee_id
LEFT JOIN users assignee ON r.assigned_to = assignee.employee_id
WHERE r.status IN ('pending', 'assigned', 'in_progress')
ORDER BY
  CASE r.priority
    WHEN 'emergency' THEN 1
    WHEN 'high' THEN 2
    WHEN 'medium' THEN 3
    WHEN 'low' THEN 4
  END,
  r.created_at ASC
LIMIT 100`
  },

  // 🌴 TIME OFF
  {
    category: 'Time Off',
    title: 'Pending Time-Off Requests',
    description: 'Requests awaiting approval, oldest first',
    sql: `SELECT
  r.id,
  u.name as employee,
  r.request_type,
  r.start_date,
  r.end_date,
  r.hours_requested,
  r.is_paid,
  LEFT(r.reason, 80) as reason,
  DATE_FORMAT(r.created_at, '%Y-%m-%d %h:%i %p') as requested_at,
  DATEDIFF(CURDATE(), DATE(r.created_at)) as days_waiting
FROM time_off_requests r
JOIN users u ON r.user_id = u.id
WHERE r.status = 'pending'
ORDER BY r.created_at ASC`
  },
  {
    category: 'Time Off',
    title: 'Approved Hours by Type (90 days)',
    description: 'Approved time-off hours grouped by type',
    sql: `SELECT
  request_type,
  COUNT(*) as request_count,
  SUM(hours_requested) as total_hours,
  SUM(CASE WHEN is_paid = 1 THEN hours_requested ELSE 0 END) as paid_hours,
  SUM(CASE WHEN is_paid = 0 THEN hours_requested ELSE 0 END) as unpaid_hours
FROM time_off_requests
WHERE status = 'approved'
  AND start_date >= DATE_SUB(CURDATE(), INTERVAL 90 DAY)
GROUP BY request_type
ORDER BY total_hours DESC`
  },

  // ☕ BREAKS
  {
    category: 'Breaks',
    title: 'Breaks Today',
    description: 'All breaks taken today with duration and shift',
    sql: `SELECT
  u.name as employee,
  b.break_type,
  DATE_FORMAT(b.start_time, '%h:%i %p') as start_time,
  DATE_FORMAT(b.end_time, '%h:%i %p') as end_time,
  b.duration_minutes,
  b.auto_completed,
  b.shift_id
FROM breaks b
JOIN users u ON b.employee_id = u.employee_id
WHERE DATE(b.start_time) = CURDATE()
ORDER BY b.start_time DESC`
  },

  // 🤝 CUSTOMERS
  {
    category: 'Customers',
    title: 'Active Customers',
    description: 'All active customers with contact info',
    sql: `SELECT
  name,
  location,
  phone,
  email,
  DATE_FORMAT(created_at, '%Y-%m-%d') as added_on
FROM customers
WHERE active = 1
ORDER BY name`
  },

  // 🔍 SYSTEM & AUDIT
  {
    category: 'System',
    title: 'Database Tables',
    description: 'List all tables in the database',
    sql: `SHOW TABLES`
  },
  {
    category: 'System',
    title: 'Table Structure',
    description: 'View schema for users table',
    sql: `DESCRIBE users`
  },
  {
    category: 'System',
    title: 'Database Size',
    description: 'Size of each table in the database',
    sql: `SELECT
  table_name,
  ROUND((data_length + index_length) / 1024 / 1024, 2) as size_mb,
  table_rows as row_count
FROM information_schema.tables
WHERE table_schema = DATABASE()
ORDER BY (data_length + index_length) DESC`
  },
  {
    category: 'System',
    title: 'Recent Shift Audits',
    description: 'Latest shift audit log entries',
    sql: `SELECT
  a.action,
  a.performed_by,
  a.creation_source,
  DATE_FORMAT(a.timestamp, '%Y-%m-%d %h:%i %p') as timestamp,
  u.name as employee_name,
  CONCAT(
    FLOOR(TIMESTAMPDIFF(MINUTE, a.timestamp, NOW()) / 1440), 'd ',
    MOD(FLOOR(TIMESTAMPDIFF(MINUTE, a.timestamp, NOW()) / 60), 24), 'h ago'
  ) as time_ago
FROM shift_audit_log a
LEFT JOIN shifts s ON a.shift_id = s.id
LEFT JOIN users u ON s.employee_id = u.employee_id
ORDER BY a.timestamp DESC
LIMIT 100`
  }
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const HISTORY_KEY = 'qc.history';
const HISTORY_MAX = 15;

/** Extract the leading SQL keyword (comment-stripped, uppercased). */
function leadingKeyword(sql: string): string {
  const stripped = sql.replace(/^(\s|--[^\n]*\n|\/\*[\s\S]*?\*\/)+/, '').trimStart();
  return /^([A-Za-z]+)/.exec(stripped)?.[1]?.toUpperCase() ?? '';
}

/** Determine if a query is a read-only or write query based on the leading keyword. */
function queryKind(sql: string): 'read' | 'write' {
  return ['SELECT', 'SHOW', 'DESCRIBE', 'EXPLAIN', 'WITH'].includes(leadingKeyword(sql)) ? 'read' : 'write';
}

/** Returns true if the query should receive an auto-appended LIMIT (SELECT/WITH only). */
function shouldAutoLimit(sql: string): boolean {
  return ['SELECT', 'WITH'].includes(leadingKeyword(sql));
}

/** Returns true if the SQL already has a real trailing LIMIT clause (not inside a string literal). */
function hasLimit(sql: string): boolean {
  const trimmed = sql.replace(/;?\s*$/, '');
  return /\blimit\s+\d+(\s*,\s*\d+)?\s*$/i.test(trimmed);
}

function loadHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHistory(entries: HistoryEntry[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(0, HISTORY_MAX)));
  } catch {
    // Ignore storage errors
  }
}

function pushHistory(entry: HistoryEntry, current: HistoryEntry[]): HistoryEntry[] {
  const next = [entry, ...current.filter((h) => h.sql !== entry.sql || h.companyId !== entry.companyId)];
  return next.slice(0, HISTORY_MAX);
}

function formatRelativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

function downloadBlob(content: string, filename: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function rowsToTsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return '';
  const cols = Object.keys(rows[0]);
  const header = cols.join('\t');
  const body = rows.map((r) => cols.map((c) => (r[c] === null ? '' : String(r[c]))).join('\t')).join('\n');
  return `${header}\n${body}`;
}

function rowsToCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return '';
  const cols = Object.keys(rows[0]);
  const escape = (v: unknown) => {
    if (v === null) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.map(escape).join(',');
  const body = rows.map((r) => cols.map((c) => escape(r[c])).join(',')).join('\n');
  return `${header}\n${body}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// Access gate (can_access_database_tools) is enforced by the parent shell (DatabaseTools).
const QueryConsoleTab: React.FC<{ isOwner?: boolean }> = ({ isOwner = false }) => {
  // Core state. The selection is a database name (or the registry alias) —
  // the same target names the migration runner uses.
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  // Read-only by default. Write mode: one statement, a fresh sign-in, a backup
  // first, the migration lock and time limits, and every statement logged.
  const [mode, setMode] = useState<'read' | 'write'>('read');
  const [writeIntro, setWriteIntro] = useState(false);
  const [writeNote, setWriteNote] = useState<string | null>(null);
  const approvalGate = useApprovalGate();
  const [prefillNote, setPrefillNote] = useState<string | null>(null);
  const [queryText, setQueryText] = useState('');
  const [queryResults, setQueryResults] = useState<Record<string, unknown>[]>([]);
  const [queryError, setQueryError] = useState<string>('');
  const [queryExecuting, setQueryExecuting] = useState(false);
  const [executionMs, setExecutionMs] = useState<number | null>(null);

  // Template sidebar state
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [templateSearch, setTemplateSearch] = useState('');

  // Results filter
  const [rowFilter, setRowFilter] = useState('');
  const [showRowFilter, setShowRowFilter] = useState(false);
  const rowFilterRef = useRef<HTMLInputElement>(null);

  // History
  const [history, setHistory] = useState<HistoryEntry[]>(() => loadHistory());

  useEffect(() => {
    fetchCompanies();
    // Hand-off from "Open in console" on a failed run.
    try {
      const raw = sessionStorage.getItem(CONSOLE_PREFILL_KEY);
      if (raw) {
        sessionStorage.removeItem(CONSOLE_PREFILL_KEY);
        const p = JSON.parse(raw) as { target?: string; sql?: string; note?: string };
        if (p.target) setSelectedCompanyId(p.target);
        if (p.sql) setQueryText(p.sql);
        setPrefillNote(p.note || 'Opened from a failed run. Read-only until you switch to write mode.');
      }
    } catch {
      /* ignore */
    }
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/super-admin/companies');
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const selectedCompany = companies.find((c) => c.database_name === selectedCompanyId) ?? null;
  const kind = queryText.trim() ? queryKind(queryText) : 'read';
  const willAutoLimit = queryText.trim() && shouldAutoLimit(queryText) && !hasLimit(queryText);

  const executeQuery = useCallback(async () => {
    if (!queryText.trim()) {
      setQueryError('Please enter a query');
      return;
    }
    if (!selectedCompanyId) {
      setQueryError('Please select a company database');
      return;
    }
    if (mode === 'read' && queryKind(queryText) === 'write') {
      setQueryError('This statement writes. Read-only mode runs SELECT / SHOW / DESCRIBE / EXPLAIN only — switch to write mode to run it.');
      return;
    }
    if (mode === 'write') {
      setQueryExecuting(true);
      setQueryError('');
      setQueryResults([]);
      setExecutionMs(null);
      setWriteNote(null);
      const t0w = performance.now();
      // Kept across a sign-in redirect (write mode needs a fresh session).
      try {
        sessionStorage.setItem(CONSOLE_PREFILL_KEY, JSON.stringify({
          target: selectedCompanyId, sql: queryText, note: 'Your write-mode query was kept while you signed in again. Switch to write mode to run it.',
        }));
      } catch { /* ignore */ }
      try {
        // Write mode needs another owner's approval for this exact statement
        // (428 → "Ask another owner"); the write runs once it is approved.
        const target = selectedCompanyId;
        const sql = queryText;
        const went = await approvalGate.guard(async (approvalId) => {
          const r = await runnerApi.consoleWrite(sql, target, approvalId);
          try { sessionStorage.removeItem(CONSOLE_PREFILL_KEY); } catch { /* ignore */ }
          setQueryResults(r.results || []);
          setExecutionMs(Math.round(performance.now() - t0w));
          setWriteNote(`Done: ${r.affectedRows ?? 0} row(s) affected. Backup ${r.backupId} taken first; saved to history with your name.`);
        });
        if (!went) setWriteNote('Not run: waiting for another owner to approve this statement. Run it again once they have.');
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } }).response?.status;
        if (status !== 401) { try { sessionStorage.removeItem(CONSOLE_PREFILL_KEY); } catch { /* ignore */ } }
        setQueryError(errorText(error, 'Write failed'));
      } finally {
        setQueryExecuting(false);
      }
      return;
    }

    // For SELECT/WITH queries with no LIMIT, auto-append LIMIT 500.
    // SHOW/DESCRIBE/EXPLAIN are read-only but do not accept a LIMIT clause.
    let sqlToRun = queryText;
    if (shouldAutoLimit(queryText) && !hasLimit(queryText)) {
      sqlToRun = queryText.trimEnd() + '\nLIMIT 500';
    }

    setQueryExecuting(true);
    setQueryError('');
    setQueryResults([]);
    setExecutionMs(null);
    setRowFilter('');
    setShowRowFilter(false);

    const t0 = performance.now();
    try {
      const response = await runnerApi.consoleRead(sqlToRun, selectedCompanyId);
      const elapsed = Math.round(performance.now() - t0);
      const results: Record<string, unknown>[] = response.results || [];
      setQueryResults(results);
      setExecutionMs(elapsed);

      // Save to history
      const entry: HistoryEntry = {
        sql: queryText,
        companyId: selectedCompanyId,
        companyName: selectedCompanyId === REGISTRY_TARGET ? 'Registry' : (selectedCompany?.company_name ?? ''),
        rows: results.length,
        at: new Date().toISOString(),
      };
      const next = pushHistory(entry, history);
      setHistory(next);
      saveHistory(next);
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } };
      setQueryError(axiosError.response?.data?.message || 'Query execution failed');
      console.error('Query execution error:', error);
    } finally {
      setQueryExecuting(false);
    }
  }, [queryText, selectedCompanyId, selectedCompany, history, mode]);

  // Cmd/Ctrl+Enter to run
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      executeQuery();
    }
  };

  // Results filter (client-side substring over stringified row values)
  const filteredResults = rowFilter.trim()
    ? queryResults.filter((row) =>
        Object.values(row).some((v) => String(v ?? '').toLowerCase().includes(rowFilter.toLowerCase()))
      )
    : queryResults;

  // Templates
  const categories = ['All', ...Array.from(new Set(queryTemplates.map((q) => q.category)))];
  const filteredTemplates = queryTemplates.filter((q) => {
    const matchesCategory = selectedCategory === 'All' || q.category === selectedCategory;
    const matchesSearch =
      templateSearch === '' ||
      q.title.toLowerCase().includes(templateSearch.toLowerCase()) ||
      q.description.toLowerCase().includes(templateSearch.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Group templates by category for the sidebar list
  const templatesByCategory: Record<string, QueryTemplate[]> = {};
  for (const t of filteredTemplates) {
    if (!templatesByCategory[t.category]) templatesByCategory[t.category] = [];
    templatesByCategory[t.category].push(t);
  }

  // Export helpers
  const handleCopyTsv = async () => {
    try {
      await navigator.clipboard.writeText(rowsToTsv(filteredResults));
    } catch {
      // Fallback: create a temporary textarea
      const ta = document.createElement('textarea');
      ta.value = rowsToTsv(filteredResults);
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  const handleDownloadCsv = () => {
    downloadBlob(rowsToCsv(filteredResults), 'query-results.csv', 'text/csv');
  };

  const handleDownloadJson = () => {
    downloadBlob(JSON.stringify(filteredResults, null, 2), 'query-results.json', 'application/json');
  };

  const handleToggleFilter = () => {
    const next = !showRowFilter;
    setShowRowFilter(next);
    if (!next) setRowFilter('');
    if (next) setTimeout(() => rowFilterRef.current?.focus(), 50);
  };

  const loadFromHistory = (entry: HistoryEntry) => {
    setQueryText(entry.sql);
    if (entry.companyId && (entry.companyId === REGISTRY_TARGET || companies.some((c) => c.database_name === entry.companyId))) {
      setSelectedCompanyId(entry.companyId);
    }
    setQueryResults([]);
    setQueryError('');
    setExecutionMs(null);
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="px-6 py-6">
      {/* Page header */}
      <div className="mb-5">
        <p className="text-xs text-slate-400 mb-0.5">Database Tools / Query Console</p>
        <h2 className="text-lg font-semibold text-slate-900">Query Console</h2>
      </div>

      {/* Two-pane layout: left (editor + results) | right (history + templates) */}
      <div className="grid gap-4" style={{ gridTemplateColumns: '1fr 240px' }}>
        {/* ================================================================
            LEFT PANE — DB select header + editor + results
        ================================================================ */}
        <div className="min-w-0 space-y-3">
          {/* Editor card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {/* Editor header: title + badge + DB selector */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600">
              <CommandLineIcon className="w-4 h-4 text-slate-400 shrink-0" />
              <span>SQL Editor</span>
              {queryText.trim() && (
                kind === 'read' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    READ STATEMENT
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-200">
                    WRITE STATEMENT
                  </span>
                )
              )}
              {mode === 'read' ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700">Read-only</span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-600 text-white">Write mode</span>
              )}
              {isOwner && (
                mode === 'read' ? (
                  <button type="button" onClick={() => setWriteIntro(true)} className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">
                    Switch to write mode…
                  </button>
                ) : (
                  <button type="button" onClick={() => { setMode('read'); setWriteNote(null); }} className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50">
                    Back to read-only
                  </button>
                )
              )}
              {/* DB selector in the header (right-aligned) */}
              <div className="ml-auto flex items-center gap-2 shrink-0">
                <CircleStackIcon className="w-4 h-4 text-slate-400" />
                <select
                  value={selectedCompanyId}
                  onChange={(e) => setSelectedCompanyId(e.target.value)}
                  className="text-xs font-semibold bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-slate-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                >
                  <option value="">— Select database —</option>
                  <option value={REGISTRY_TARGET}>Registry</option>
                  {companies.filter((company) => company.database_name).map((company) => (
                    <option key={company.id} value={company.database_name!}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Textarea */}
            <textarea
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={"Enter your SQL query here…\n\nExample: SELECT * FROM users LIMIT 10"}
              className="w-full px-4 py-3.5 text-[12.5px] font-mono text-slate-800 placeholder-slate-400 border-none outline-none resize-y min-h-[150px]"
              rows={8}
            />

            {/* Editor toolbar */}
            <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-t border-slate-200">
              <button
                onClick={executeQuery}
                disabled={queryExecuting || !queryText.trim() || !selectedCompanyId}
                className="inline-flex items-center gap-2 px-3.5 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {queryExecuting ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Executing…
                  </>
                ) : (
                  <>
                    <PlayIcon className="w-3.5 h-3.5" />
                    Run
                    <span className="font-mono text-[10px] bg-blue-500/60 px-1.5 py-0.5 rounded">
                      {navigator.userAgent.toLowerCase().includes('mac') ? '⌘↵' : 'Ctrl↵'}
                    </span>
                  </>
                )}
              </button>
              <button
                onClick={() => {
                  setQueryText('');
                  setQueryResults([]);
                  setQueryError('');
                  setExecutionMs(null);
                  setRowFilter('');
                  setShowRowFilter(false);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <TrashIcon className="w-3.5 h-3.5" />
                Clear
              </button>
              {willAutoLimit && (
                <span className="ml-auto text-[11px] text-slate-400">
                  Auto-adds{' '}
                  <code className="font-mono bg-slate-100 px-1 rounded text-slate-500">LIMIT 500</code>{' '}
                  if none set
                </span>
              )}
            </div>
          </div>

          {prefillNote && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">{prefillNote}</div>
          )}
          {mode === 'write' && (
            <div className="rounded-lg border-[1.5px] border-amber-400 bg-amber-50 p-3 text-xs text-amber-900">
              <b>Write mode.</b> One statement per run. A backup of this database is taken first, it runs under the same per-database lock
              and time limits as migrations, and every statement is saved to history with your name.
            </div>
          )}
          {writeNote && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-xs text-green-800">{writeNote}</div>
          )}

          {/* Error */}
          {queryError && (
            <div className="flex items-center gap-3 p-3.5 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              <span className="whitespace-pre-wrap break-words">{queryError}</span>
            </div>
          )}

          {/* Results card */}
          {(queryResults.length > 0 || executionMs !== null) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Results header */}
              <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-xs">
                <span className="font-semibold text-slate-700">Results</span>
                {executionMs !== null && (
                  <span className="text-slate-500">
                    {filteredResults.length !== queryResults.length
                      ? `${filteredResults.length} of ${queryResults.length} rows`
                      : `${queryResults.length} row${queryResults.length !== 1 ? 's' : ''}`}
                    {' · '}
                    {executionMs} ms
                  </span>
                )}
                {/* Results toolbar */}
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    onClick={handleToggleFilter}
                    className={`inline-flex items-center gap-1 text-[11px] border rounded-lg px-2 py-1 font-semibold transition-colors ${
                      showRowFilter
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <MagnifyingGlassIcon className="w-3 h-3" />
                    Filter
                  </button>
                  <button
                    onClick={handleCopyTsv}
                    className="inline-flex items-center gap-1 text-[11px] border border-slate-300 bg-white rounded-lg px-2 py-1 font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                    title="Copy as TSV"
                  >
                    Copy
                  </button>
                  <button
                    onClick={handleDownloadCsv}
                    className="inline-flex items-center gap-1 text-[11px] border border-slate-300 bg-white rounded-lg px-2 py-1 font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                    title="Download CSV"
                  >
                    ↓ CSV
                  </button>
                  <button
                    onClick={handleDownloadJson}
                    className="inline-flex items-center gap-1 text-[11px] border border-slate-300 bg-white rounded-lg px-2 py-1 font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
                    title="Download JSON"
                  >
                    ↓ JSON
                  </button>
                </div>
              </div>

              {/* Inline filter input */}
              {showRowFilter && (
                <div className="px-4 py-2 border-b border-slate-100 bg-slate-50">
                  <input
                    ref={rowFilterRef}
                    type="text"
                    value={rowFilter}
                    onChange={(e) => setRowFilter(e.target.value)}
                    placeholder="Filter rows…"
                    className="w-full text-xs px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                  />
                </div>
              )}

              {/* Table */}
              <div className="overflow-auto max-h-96">
                {filteredResults.length > 0 ? (
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                        {Object.keys(filteredResults[0]).map((key) => (
                          <th
                            key={key}
                            className="text-left text-[9px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2 whitespace-nowrap"
                          >
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredResults.map((row, index) => (
                        <tr key={index} className="hover:bg-slate-50 transition-colors">
                          {Object.values(row).map((value, idx) => (
                            <td
                              key={idx}
                              className="px-3 py-2 text-slate-700 whitespace-nowrap"
                            >
                              {value === null ? (
                                <span className="text-slate-300 italic">NULL</span>
                              ) : (
                                String(value)
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-10 text-sm text-slate-400">
                    No rows match the filter
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ================================================================
            RIGHT PANE — Recent queries + Templates
        ================================================================ */}
        <div className="space-y-3 min-w-0">
          {/* Recent queries card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600">
              🕘 Recent queries
            </div>
            {history.length === 0 ? (
              <p className="text-[11px] text-slate-400 text-center py-5 px-3">No queries yet</p>
            ) : (
              <div>
                {history.map((entry) => (
                  <button
                    key={`${entry.at}-${entry.companyId}`}
                    onClick={() => loadFromHistory(entry)}
                    className="w-full text-left px-3 py-2 border-t border-slate-100 hover:bg-slate-50 transition-colors first:border-t-0"
                  >
                    <code className="block text-[10.5px] font-mono text-slate-700 truncate">
                      {entry.sql.replace(/\s+/g, ' ').slice(0, 60)}
                      {entry.sql.length > 60 ? '…' : ''}
                    </code>
                    <span className="text-[9.5px] text-slate-400 mt-0.5 block">
                      {entry.companyName ? `${entry.companyName} · ` : ''}
                      {formatRelativeTime(entry.at)} · {entry.rows} row{entry.rows !== 1 ? 's' : ''}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Templates card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-600">
              📚 Templates
            </div>

            {/* Template search */}
            <div className="px-2.5 py-2">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search templates…"
                  value={templateSearch}
                  onChange={(e) => setTemplateSearch(e.target.value)}
                  className="w-full pl-7 pr-2.5 py-1.5 text-[11px] border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow"
                />
              </div>
            </div>

            {/* Category filter pills */}
            <div className="px-2.5 pb-2 flex flex-wrap gap-1">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors ${
                    selectedCategory === cat
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-500 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Template list grouped by category */}
            <div className="border-t border-slate-100 max-h-[460px] overflow-y-auto">
              {Object.entries(templatesByCategory).length === 0 ? (
                <p className="text-[11px] text-slate-400 text-center py-6 px-3">No templates match</p>
              ) : (
                Object.entries(templatesByCategory).map(([cat, templates]) => (
                  <div key={cat}>
                    <div className="px-3 pt-2.5 pb-1 text-[9px] font-bold uppercase tracking-wider text-slate-400">
                      {cat}
                    </div>
                    {templates.map((template, idx) => (
                      <button
                        key={idx}
                        onClick={() => setQueryText(template.sql)}
                        className="w-full text-left flex items-center gap-2 px-3 py-1.5 border-t border-slate-50 hover:bg-slate-50 transition-colors"
                      >
                        <span className="text-[11.5px] text-slate-700 truncate">{template.title}</span>
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={writeIntro}
        onClose={() => setWriteIntro(false)}
        title="Switch to write mode?"
        footer={
          <>
            <Btn kind="ghost" onClick={() => setWriteIntro(false)}>Stay read-only</Btn>
            <Btn kind="danger" onClick={() => { setMode('write'); setWriteIntro(false); }}>Switch to write mode</Btn>
          </>
        }
      >
        <p>Write mode changes data directly. Each run:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>needs a recent sign-in (you may be asked to sign in again — your query is kept on this page),</li>
          <li>takes a backup of the database first, and refuses to run if that fails,</li>
          <li>runs one statement under the migration lock with the same time limits,</li>
          <li>needs another owner to approve that exact statement (when the Safety tab says so),</li>
          <li>is saved to history with your name.</li>
        </ul>
      </Dialog>
      {approvalGate.element}
    </div>
  );
};

export default QueryConsoleTab;
